import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";

import { PROACTIVE_COMPARISON_PLAN_FEATURES } from "@/components/modal/paywall/plan-features";
import {
  buildProactivePlanCard,
  getProactivePlanActionCopy,
  PROACTIVE_MONEY_BACK_GUARANTEE_NOTE,
} from "@/components/modal/proactive-upsell/proactive-plan";
import {
  MDSBadge,
  MDSBadgeVariant,
  MDSButton,
  MDSButtonVariant,
  MDSIcon,
  MDSIconKind,
  MDSPlanCard,
  MDSPlanCardBackground,
  MDSPlanCardBorder,
  MDSPlanCardSlot,
  MDSPlanId,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";

interface Props {
  badgeLabel?: string;
  isTrialEligible: boolean;
  proactivePriceText: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  onSubscribe: () => void;
}

export const ProactiveSubscriptionPlanCard: React.FC<Props> = observer(
  ({ badgeLabel, isTrialEligible, proactivePriceText, isLoading, isDisabled, onSubscribe }) => {
    const planCard = buildProactivePlanCard({
      badgeLabel,
      isTrialEligible,
      proactivePrice: proactivePriceText,
    });
    const actionCopy = getProactivePlanActionCopy({
      isTrialEligible,
      proactivePrice: proactivePriceText,
      pricingIsLoading: false,
    });

    return (
      <MDSPlanCard
        plan={MDSPlanId.Paid}
        features={PROACTIVE_COMPARISON_PLAN_FEATURES}
        border={MDSPlanCardBorder.Emphasis}
        background={MDSPlanCardBackground.Emphasis}
      >
        <MDSText
          variant={MDSTextVariant.HeaderL}
          weight={MDSTextWeight.SemiBold}
          slot={MDSPlanCardSlot.Title}
        >
          {planCard.title}
        </MDSText>
        {planCard.badgeLabel ? (
          <MDSBadge
            label={planCard.badgeLabel}
            variant={MDSBadgeVariant.MoreEmphasis}
            slot={MDSPlanCardSlot.TitleBadge}
          />
        ) : null}
        <div slot={MDSPlanCardSlot.Price}>{planCard.price}</div>
        {planCard.priceSubtitle ? (
          <div slot={MDSPlanCardSlot.PriceTerm}>{planCard.priceSubtitle}</div>
        ) : null}

        <PlanCardFooter slot={MDSPlanCardSlot.Footer}>
          <MDSButton
            variant={MDSButtonVariant.Emphasis}
            isLoading={isLoading}
            isDisabled={isDisabled}
            onClick={onSubscribe}
          >
            <MDSIcon kind={MDSIconKind.StarSolid} /> {actionCopy.buttonLabel}
          </MDSButton>
          <ActionNote variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
            {PROACTIVE_MONEY_BACK_GUARANTEE_NOTE}
          </ActionNote>
        </PlanCardFooter>
      </MDSPlanCard>
    );
  }
);

const PlanCardFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const ActionNote = styled(MDSText)`
  text-align: center;
`;
